import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  UserPlus, 
  Heart, 
  MessageSquare, 
  Award, 
  Download, 
  Bell, 
  Check 
} from "lucide-react";

interface NotificationItem {
  id: number;
  type: string;
  message: string;
  url: string;
  isRead: boolean;
  createdAt: string;
  senderNickname: string;
}

interface NotificationDropdownProps {
  notifications: NotificationItem[];
  unreadCount: number;
  onRead: (id: number, url: string) => void;
  onReadAll: () => void;
  onClose: () => void;
  dropdownRef: React.RefObject<HTMLDivElement>;
}

export function formatTimeAgo(dateString: string): string {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  
  if (isNaN(diffMs)) {
    return "";
  }

  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "방금 전";
  if (diffMins < 60) return `${diffMins}분 전`;
  if (diffHours < 24) return `${diffHours}시간 전`;
  return `${diffDays}일 전`;
}

export const getNotificationStyles = (type: string) => {
  switch (type) {
    case "FOLLOW":
      return {
        bg: "bg-[#F3E8FF]",
        text: "text-[#A855F7]",
        icon: UserPlus,
      };
    case "BOOK_LIKE":
    case "POSTCARD_LIKE":
    case "COMMENT_LIKE":
      return {
        bg: "bg-[#FFE4E6]",
        text: "text-[#F43F5E]",
        icon: Heart,
      };
    case "COMMENT":
    case "REPLY":
      return {
        bg: "bg-[#DBEAFE]",
        text: "text-[#3B82F6]",
        icon: MessageSquare,
      };
    case "AUTHOR_NOTE_VIEW_MILESTONE":
      return {
        bg: "bg-[#FFEDD5]",
        text: "text-[#F97316]",
        icon: Award,
      };
    case "POSTCARD_DOWNLOAD":
      return {
        bg: "bg-[#F3F4F6]",
        text: "text-[#4B5563]",
        icon: Download,
      };
    default:
      return {
        bg: "bg-green-100",
        text: "text-green-600",
        icon: Bell,
      };
  }
};

const NotificationDropdown = ({
  notifications,
  unreadCount,
  onRead,
  onReadAll,
  onClose,
  dropdownRef,
}: NotificationDropdownProps) => {
  const navigate = useNavigate();

  // 화면 너비 감지 상태 추가 (포지션 겹침 방지)
  const [isWindowMobile, setIsWindowMobile] = React.useState(window.innerWidth < 640);

  React.useEffect(() => {
    const handleResize = () => {
      setIsWindowMobile(window.innerWidth < 640);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleItemClick = (item: NotificationItem) => {
    onRead(item.id, item.url);
    onClose();
  };

  const handleMoreClick = () => {
    navigate("/notifications");
    onClose();
  };

  // 모바일과 PC 스타일 분기
  const containerClass = isWindowMobile
    ? "fixed top-[85px] left-4 right-4 w-[calc(100vw-32px)] rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.16)] border border-[#E5E7EB] z-[9999] overflow-hidden flex flex-col"
    : "absolute right-0 top-[80px] w-[380px] rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.16)] border border-[#E5E7EB] z-[9999] overflow-hidden flex flex-col";

  return (
    <div
      ref={dropdownRef}
      className={containerClass}
      style={{
        fontFamily: "Pretendard, sans-serif",
        backgroundColor: "#ffffff"
      }}
    >
      {/* 헤더 */}
      <div className="flex items-center justify-between p-4 border-b border-[#F3F4F6]">
        <div className="flex items-center gap-2">
          <span className="font-bold text-lg text-[#111827]">알림</span>
          {unreadCount > 0 && (
            <span className="flex items-center justify-center bg-[#19CF7A] text-white text-xs font-semibold px-2 py-0.5 rounded-full min-w-[20px] h-[20px]">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={onReadAll}
            className="flex items-center gap-1 text-sm font-semibold text-[#19CF7A] hover:opacity-80 transition-opacity"
          >
            <Check size={16} />
            모두 읽음
          </button>
        )}
      </div>

      {/* 알림 목록 */}
      <div className="max-h-[340px] overflow-y-auto divide-y divide-[#F9FAFB]">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-[#9CA3AF] gap-2">
            <Bell size={32} className="stroke-[1.5]" />
            <span className="text-sm">수신된 알림이 없습니다.</span>
          </div>
        ) : (
          notifications.map((item) => {
            const styles = getNotificationStyles(item.type);
            const Icon = styles.icon;
            // read 와 isRead 동시 연동
            const isNotificationRead = item.isRead ?? (item as any).read ?? false;

            return (
              <div
                key={item.id}
                onClick={() => handleItemClick(item)}
                className={`flex items-start gap-3 p-4 cursor-pointer transition-colors duration-150 relative ${
                  isNotificationRead ? "bg-white hover:bg-[#F9FAFB]" : "bg-[#F4FBF7] hover:bg-[#EDF8F2]"
                }`}
              >
                {/* 아이콘 */}
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${styles.bg} ${styles.text}`}>
                  <Icon size={18} />
                </div>

                {/* 텍스트 내용 */}
                <div className="flex-grow pr-3">
                  <p className="text-sm text-[#374151] leading-relaxed break-words font-medium">
                    {item.message}
                  </p>
                  <span className="text-xs text-[#9CA3AF] mt-1 block">
                    {formatTimeAgo(item.createdAt)}
                  </span>
                </div>

                {/* 미확인 표시 점 */}
                {!isNotificationRead && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-[#19CF7A]"></div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* 푸터 (더보기) */}
      <button
        onClick={handleMoreClick}
        className="w-full py-3 bg-white text-center text-sm font-semibold text-[#4B5563] border-t border-[#F3F4F6] hover:bg-[#F9FAFB] transition-colors"
      >
        더보기
      </button>
    </div>
  );
};

export default NotificationDropdown;
